import { Component, OnChanges, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';

import { of, switchMap, take } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { DisplayProperty } from 'src/models/displayProperty';
import { ECU } from 'src/models/ecu';
import { Property } from 'src/models/property';
import { EcuService } from 'src/services/ecu.service';
import { TestRunBaseService } from 'src/services/test-run.base.service';

@Component({
	selector: 'app-test-run-properties',
	templateUrl: './test-run-properties.component.html',
	styleUrls: ['./test-run-properties.component.scss'],
})
export class TestRunPropertiesComponent implements OnChanges {
	constructor(
		private testRunBaseService: TestRunBaseService,
		private ecuService: EcuService,
		private loggerService: TmtLoggerService,
	) {}

	displayedColumns: string[] = ['name', 'value', 'description'];

	dataSource: MatTableDataSource<DisplayProperty>;

	maxall = 10;

	@ViewChild(MatPaginator) paginator: MatPaginator;

	@ViewChild(MatSort) sort: MatSort;

	@Input() runUid: string;

	@Output() countRunProperties: EventEmitter<any> = new EventEmitter();

	ngOnChanges() {
		this.getProperties(this.runUid);
	}

	private getProperties(runUid: string) {
		this.testRunBaseService
			.getTestRunProperties(runUid)
			.pipe(
				take(1),
				switchMap(properties => {
					const needsEcuLookup = properties.some(p => p.name === 'EcuForSoftwareVersion');
					if (needsEcuLookup) {
						return this.ecuService.allEcus$.pipe(
							take(1),
							switchMap(ecus => of({ properties, ecus })),
						);
					}
					return of({ properties, ecus: [] as ECU[] });
				}),
			)
			.subscribe({
				next: ({ properties, ecus }) => {
					const displayProperties = properties.map(p => this.mapProperty(p, ecus));
					this.countRunProperties.emit(displayProperties.length);
					this.dataSource = new MatTableDataSource<DisplayProperty>(displayProperties);
					this.dataSource.paginator = this.paginator;
					this.dataSource.sort = this.sort;
				},
				error: error => this.loggerService.logError(error),
			});
	}

	private mapProperty(property: Property, ecus: ECU[]): DisplayProperty {
		let displayName = property.name;
		let displayValue = property.value;

		if (property.name === 'SoftwareVersion') {
			displayName = 'SW version';
		} else if (property.name === 'EcuForSoftwareVersion') {
			displayName = 'ECU';
			const ecuId = Number(property.value);
			const ecu = ecus.find(e => e.id === ecuId);
			displayValue = ecu ? ecu.name : property.value;
		}

		return {
			displayName,
			displayValue,
			description: property.description,
			fieldFormat: property.fieldFormat,
		};
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}
}
